// client/src/components/leagues/StandingsTable.js
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatStandingsForDisplay } from '../../utils/standings'
import StandingsHelpModal from './StandingsHelpModal'
import InfoTooltip from '../common/InfoTooltip'
import './StandingsTable.css'
import { MINIMUM_MATCHES_FOR_ELIGIBILITY } from '../../utils/pointsCalculation'

/**
 * Reusable standings table component
 * @param {Object} props
 * @param {Array} props.standings - Array of player standings
 * @param {string} props.title - Table title
 * @param {boolean} props.showDivision - Show division column
 * @param {boolean} props.showSets - Show set statistics
 * @param {boolean} props.showGames - Show game statistics
 * @param {boolean} props.compact - Use compact styling
 * @param {Function} props.onPlayerClick - Callback when player is clicked
 * @param {Function} props.onHelpClick - Callback when help button is clicked
 * @param {number} props.leagueId - League ID for navigation context (optional)
 * @param {number} props.divisionId - Division ID for navigation context (optional)
 */
export default function StandingsTable({
  standings = [],
  title = 'Standings',
  showDivision = false,
  showSets = false,
  showGames = false,
  compact = false,
  onPlayerClick = null,
  onHelpClick = null,
  leagueId = null,
  divisionId = null,
  matchFormat = 'TRADITIONAL',
  minimumMatchesForEligibility = MINIMUM_MATCHES_FOR_ELIGIBILITY,
}) {
  const [showHelpModal, setShowHelpModal] = useState(false)

  const formattedStandings = formatStandingsForDisplay(standings, {
    showDivision,
    showSets,
    showGames,
    matchFormat,
  })

  const handleHelpClick = () => {
    if (onHelpClick) {
      onHelpClick()
    } else {
      setShowHelpModal(true)
    }
  }

  if (standings.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p>No standings data available.</p>
      </div>
    )
  }

  const tableClass = compact ? 'table table-sm mb-0' : 'table'
  const headerClass = compact ? 'text-muted small' : 'text-muted'

  return (
    <div className="standings-table">
      {title && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0">
            <span className="me-2">🏆</span>
            {title}
            <span
              onClick={handleHelpClick}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleHelpClick()
                }
              }}
              className="standings-help-trigger"
              role="button"
              tabIndex={0}
              data-testid="standings-help-info"
            >
              <InfoTooltip
                content="Click to learn how standings are calculated"
                placement="right"
              />
            </span>
          </h5>
        </div>
      )}

      <div className="table-responsive">
        <table className={tableClass} data-testid="standings-table">
          <thead>
            <tr className={headerClass}>
              <th className="border-0 fw-normal">Rank</th>
              <th className="border-0 fw-normal">Player</th>
              {showDivision && <th className="border-0 fw-normal">Division</th>}
              <th className="border-0 fw-normal text-center">Points</th>
              <th className="border-0 fw-normal text-end">Matches</th>
              <th className="border-0 fw-normal text-end">Record</th>
              <th
                className="border-0 fw-normal text-center"
                data-testid="eligibility-header"
              >
                Playoff Eligible
              </th>
              {showSets && (
                <>
                  <th className="border-0 fw-normal text-end">Sets</th>
                  <th className="border-0 fw-normal text-end">Set %</th>
                </>
              )}
              {showGames && (
                <>
                  <th className="border-0 fw-normal text-end">Games</th>
                  <th className="border-0 fw-normal text-end">Game %</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {formattedStandings.map((stat) => {
              const isTop3 = stat.rank <= 3 && !stat.isIneligible
              const rankDisplay =
                stat.rank === 1
                  ? '🏆'
                  : stat.rank === 2
                    ? '🥈'
                    : stat.rank === 3
                      ? '🥉'
                      : stat.rank

              return (
                <tr
                  key={stat.playerId}
                  className={`${stat.isCurrentPlayer ? 'bg-light' : ''} ${onPlayerClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onPlayerClick && onPlayerClick(stat)}
                  data-testid={`standings-row-${stat.playerId}`}
                >
                  <td
                    className={`${stat.isCurrentPlayer ? 'fw-bold' : ''} ${
                      isTop3 ? 'text-primary' : ''
                    }`}
                    aria-label={
                      stat.rank === 1
                        ? 'Rank 1, First Place'
                        : stat.rank === 2
                          ? 'Rank 2, Second Place'
                          : stat.rank === 3
                            ? 'Rank 3, Third Place'
                            : `Rank ${stat.rank}`
                    }
                  >
                    {rankDisplay}
                  </td>
                  <td className={stat.isCurrentPlayer ? 'fw-bold' : ''}>
                    <Link
                      to={`/players/${stat.playerId}/history`}
                      state={
                        leagueId
                          ? { fromLeague: true, leagueId, divisionId }
                          : undefined
                      }
                      className="text-decoration-none player-name-cell"
                      title={`View ${stat.playerName}'s match history`}
                    >
                      {stat.playerName}
                    </Link>
                  </td>
                  {showDivision && (
                    <td className="text-muted small">{stat.divisionName}</td>
                  )}
                  <td className="text-center fw-bold" data-testid="points-cell">
                    {stat.points}
                  </td>
                  <td className="text-end">{stat.matchesPlayed}</td>
                  <td className="text-end">{stat.record}</td>
                  <td className="text-center" data-testid="eligibility-cell">
                    {stat.isIneligible ? (
                      <span
                        className="text-muted"
                        title={`Needs ${Math.max(
                          minimumMatchesForEligibility - stat.matchesPlayed,
                          0,
                        )} more ${
                          minimumMatchesForEligibility - stat.matchesPlayed ===
                          1
                            ? 'match'
                            : 'matches'
                        }`}
                      >
                        ✗
                      </span>
                    ) : (
                      <span
                        className="text-success"
                        title="Eligible for playoffs"
                      >
                        ✓
                      </span>
                    )}
                  </td>
                  {showSets && (
                    <>
                      <td className="text-end">{stat.setRecord}</td>
                      <td className="text-end">{stat.setWinPercentage}%</td>
                    </>
                  )}
                  {showGames && (
                    <>
                      <td className="text-end">{stat.gameRecord}</td>
                      <td className="text-end">{stat.gameWinPercentage}%</td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <StandingsHelpModal
        show={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        matchFormat={matchFormat}
        minimumMatchesForEligibility={minimumMatchesForEligibility}
      />
    </div>
  )
}
